import { useEffect, useState } from 'react';
import { useApp } from '../context/AppContext';
import { PageHeader } from '../components/ui/PageHeader';
import { Button } from '../components/ui/Button';
import { SettingsList, SettingsItem, SettingsStatGrid, StatusBadge } from '../components/ui/SettingsItem';
import { API_BASE_URL } from '../api';
import { BookOpen, CircleHelp, FileDown, FileText, Languages, Palette, RefreshCw, Trash2, User, Wifi, Calendar } from 'lucide-react';
import { DELETE_DATA_URL, getHelpUrl, HELP_PDF_URL, PRIVACY_URL, TERMS_URL } from '../constants/contact';
import { openExternalUrl } from '../utils/openExternalUrl';
import { formatSubscriptionDate, isSubscriptionExpired, daysUntilExpiry } from '../utils/academicYear';
import { ThemeToggle } from '../components/ui/ThemeToggle';

export function HeadSettings() {
  const {
    labels,
    userInfo,
    classes,
    teachers,
    fetchClasses,
    fetchTeachers,
    isLoading,
    useMalayalam,
    setUseMalayalam,
  } = useApp();
  const [apiStatus, setApiStatus] = useState('checking');

  const divisionLimit = userInfo?.divisions ?? 0;
  const classCount = classes.length;
  const teacherCount = teachers.length;
  const unassignedClasses = classes.filter((c) => !c.teacher_id).length;
  const apiOnline = apiStatus === 'online';
  const currentLanguage = useMalayalam ? labels.langMalayalam : labels.langEnglish;

  const checkApi = async () => {
    setApiStatus('checking');
    try {
      const res = await fetch(`${API_BASE_URL}/health`, { method: 'GET' });
      setApiStatus(res.ok ? 'online' : 'error');
    } catch {
      setApiStatus('offline');
    }
  };

  const refreshAll = () => {
    checkApi();
    fetchClasses();
    fetchTeachers();
  };

  useEffect(() => {
    checkApi();
  }, []);

  return (
    <div className="space-y-4 pb-8">
      <PageHeader title={labels.headSettingsTitle} subtitle={labels.headSettingsSubtitle} />

      <SettingsList>
        <SettingsStatGrid
          items={[
            { label: labels.numDivisionsLabel, value: divisionLimit },
            { label: labels.classesTitle, value: `${classCount}/${divisionLimit || '—'}` },
            { label: labels.subscriptionValidUntil, value: formatSubscriptionDate(userInfo?.subscription_expires_at) },
            { label: labels.unassignedClasses, value: unassignedClasses },
          ]}
        />

        {userInfo?.subscription_expires_at && (
          <SettingsItem
            icon={Calendar}
            title={labels.subscriptionValidUntil}
            subtitle={
              isSubscriptionExpired(userInfo.subscription_expires_at)
                ? labels.subscriptionExpired
                : (() => {
                    const d = daysUntilExpiry(userInfo.subscription_expires_at);
                    return d !== null && d <= 14
                      ? `${labels.subscriptionExpiringSoon} (${d}d)`
                      : formatSubscriptionDate(userInfo.subscription_expires_at);
                  })()
            }
          />
        )}

        <SettingsItem
          icon={Wifi}
          title={labels.apiConnection}
          action={
            apiStatus === 'checking' ? (
              <span className="text-xs text-muted">{labels.loading}</span>
            ) : (
              <StatusBadge online={apiOnline} labels={labels} />
            )
          }
        />

        <SettingsItem
          icon={Palette}
          title={labels.appearance}
          subtitle={labels.appearanceHint}
          action={<ThemeToggle useMalayalam={useMalayalam} />}
        />

        <SettingsItem
          icon={Languages}
          title={labels.appLanguage}
          subtitle={currentLanguage}
          action={
            <Button variant="secondary" size="sm" onClick={() => setUseMalayalam(!useMalayalam)}>
              {labels.toggleLang}
            </Button>
          }
        />

        {userInfo?.name && (
          <SettingsItem icon={User} title={labels.signedInAs} subtitle={userInfo.name} />
        )}

        <SettingsItem
          icon={RefreshCw}
          title={labels.refreshData}
          subtitle={labels.refreshDataHint}
          action={
            <Button
              variant="secondary"
              size="sm"
              onClick={refreshAll}
              isLoading={isLoading.classes || isLoading.teachers}
            >
              {labels.refresh}
            </Button>
          }
        />

        <SettingsItem
          icon={CircleHelp}
          title={labels.helpLink}
          subtitle={labels.helpLinkHint}
          onClick={() => openExternalUrl(getHelpUrl(useMalayalam))}
        />

        <SettingsItem
          icon={FileDown}
          title={labels.helpPdfDownload}
          subtitle={labels.helpPdfDownloadHint}
          onClick={() => openExternalUrl(HELP_PDF_URL)}
        />

        <SettingsItem
          icon={FileText}
          title={labels.termsLink}
          onClick={() => openExternalUrl(TERMS_URL)}
        />

        <SettingsItem
          icon={BookOpen}
          title={labels.privacyLink}
          onClick={() => openExternalUrl(PRIVACY_URL)}
        />

        <SettingsItem
          icon={Trash2}
          title={labels.deleteDataLink}
          subtitle={labels.deleteDataHint}
          onClick={() => openExternalUrl(DELETE_DATA_URL)}
        />
      </SettingsList>
    </div>
  );
}
